import { CheckCircle } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'
import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { useSessionStore } from '@/lib/auth/session-store'
import { syncLocaleFromProfile } from '@/features/auth/session'
import { authApi } from '@/features/auth/api'
import { useGateStore } from '@/features/auth/state/gate-store'

import {
  profileSocialApi,
  type PrivateProfile,
  type UpdateProfileRequest,
} from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { EditProfileSheet } from '../components/edit-profile-sheet'
import { SettingsBackLink } from '../components/settings-back-link'

type AccountState =
  | { status: 'loading' }
  | { status: 'ready'; profile: PrivateProfile }
  | { status: 'error'; error: unknown }

interface NoticeState {
  tone: 'success' | 'error'
  key: string
  values?: Record<string, string | number>
}

function AccountNotice({ notice }: { notice: NoticeState }) {
  const { t } = useTranslation()
  if (notice.tone !== 'success') return null
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md bg-mint px-4 py-3 text-sm font-semibold text-teal"
    >
      <CheckCircle size={18} weight="bold" aria-hidden="true" className="mt-0.5" />
      {t(notice.key, notice.values)}
    </div>
  )
}

/**
 * Account (D-099): email identity, edit profile, change-password bridge and
 * the account lifecycle. The frozen contract has no inline email/password
 * swap, so the email row shows the current address with the
 * resend-verification action and the password row requests the reset link for
 * the account email (`POST /auth/forgot-password`); both mirror the original
 * flows.
 */
export function SettingsAccountScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = i18n.language === 'en' ? 'en' : 'it'

  const [state, setState] = useState<AccountState>({ status: 'loading' })
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [passwordSent, setPasswordSent] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const hasProfileRef = useRef(false)

  useEffect(() => {
    const controller = new AbortController()
    hasProfileRef.current = false
    void profileSocialApi
      .getMe({ signal: controller.signal })
      .then((profile) => {
        if (controller.signal.aborted) return
        hasProfileRef.current = true
        setState({ status: 'ready', profile })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (hasProfileRef.current) {
          setNotice({ tone: 'error', key: friendlyErrorKey(error) })
        } else {
          setState({ status: 'error', error })
        }
      })
    return () => controller.abort()
  }, [reloadKey])

  const retry = useCallback(() => {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const applySubset = useCallback(
    (subset: Awaited<ReturnType<typeof profileSocialApi.updateMe>>) => {
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', profile: { ...current.profile, ...subset } }
          : current,
      )
    },
    [],
  )

  const handleSave = useCallback(
    async (body: UpdateProfileRequest) => {
      setEditBusy(true)
      setEditError(null)
      setNotice(null)
      try {
        const subset = await profileSocialApi.updateMe(body)
        applySubset(subset)
        await syncLocaleFromProfile(subset.locale ?? locale)
        setEditOpen(false)
        setNotice({ tone: 'success', key: 'profileSocial.profile.saved' })
      } catch (error) {
        setEditError(
          error instanceof ApiError && error.status === 409
            ? 'profileSocial.profile.editErrors.nicknameTaken'
            : friendlyErrorKey(error),
        )
      } finally {
        setEditBusy(false)
      }
    },
    [applySubset, locale],
  )

  async function handleSendPasswordLink() {
    if (state.status !== 'ready' || passwordBusy) return
    setPasswordBusy(true)
    setPasswordSent(false)
    setNotice(null)
    try {
      await authApi.forgotPassword(state.profile.email)
      setPasswordSent(true)
    } catch (error) {
      // The endpoint returns 200 for unknown addresses as well; a real error
      // keeps the row actionable instead of pretending success.
      setNotice({ tone: 'error', key: friendlyErrorKey(error) })
    } finally {
      setPasswordBusy(false)
    }
  }


  const handleDelete = useCallback(async () => {
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      await profileSocialApi.deleteMe()
      useSessionStore.getState().clearSession()
      useGateStore.getState().resetGates()
      // Let the guest-state render settle before redirecting; otherwise the
      // route guard can replace the current route with `/login`.
      await Promise.resolve()
      navigate('/onboarding', { replace: true })
    } catch (error) {
      setDeleteError(friendlyErrorKey(error))
    } finally {
      setDeleteBusy(false)
    }
  }, [navigate])


  const profile = state.status === 'ready' ? state.profile : null

  return (
    <section
      aria-labelledby="settings-account-title"
      className="content-width min-h-dvh pb-10 pt-[var(--safe-top)]"
    >
      <div className="mx-auto w-full max-w-[640px] px-0.5 md:px-0">
        <div className="flex items-center gap-2 py-3">
          <SettingsBackLink to="/settings" />
          <h1
            id="settings-account-title"
            className="text-[28px] leading-tight font-bold"
          >
            {t('screens.settings.account')}
          </h1>
        </div>

        {notice ? (
          <div className="mt-3">
            <AccountNotice notice={notice} />
          </div>
        ) : null}

        {state.status === 'loading' ? (
          <LoadingState variant="form" labelKey="profileSocial.profile.loading" />
        ) : state.status === 'error' ? (
          <ErrorState
            error={state.error}
            titleKey="screens.settings.accountLoadErrorTitle"
            bodyKey="screens.settings.accountLoadErrorBody"
            onRetry={retry}
            retryLabelKey="profileSocial.profile.retry"
          />
        ) : profile ? (
          <div className="mt-4 space-y-3" data-testid="account-rows">
            <div className="rounded-[18px] border border-line bg-white p-4">
              <dl className="space-y-2.5" data-testid="account-email-row">
                {[
                  {
                    key: 'name',
                    label: t('profileSocial.profile.publicNameLabel', {
                      name: '',
                    }).replace(/\s*:.*$/, ''),
                    value: [profile.firstName, profile.lastName]
                      .map((part) => part.trim())
                      .filter(Boolean)
                      .join(' ') || profile.publicName,
                  } as const,
                  {
                    key: 'email',
                    label: t('profileSocial.profile.fields.email'),
                    value: profile.email,
                  } as const,
                  {
                    key: 'nickname',
                    label: t('profileSocial.profile.fields.nickname'),
                    value: profile.nickname,
                  } as const,
                  {
                    key: 'city',
                    label: t('profileSocial.profile.fields.city'),
                    value: profile.city,
                  } as const,
                  {
                    key: 'age',
                    label: t('profileSocial.profile.fields.age'),
                    value:
                      profile.age != null
                        ? t('profileSocial.profile.ageYears', {
                            count: profile.age,
                          })
                        : null,
                  } as const,

                ].map((item) => (
                  <div
                    key={item.key}
                    className="flex items-start justify-between gap-3 border-b border-line pb-2.5 last:border-b-0 last:pb-0"
                  >
                    <dt className="text-sm font-bold text-deep">
                      {item.label}
                    </dt>
                    <dd className="min-w-0 break-all text-right text-sm text-muted">
                      {item.value
                        ? item.value
                        : t('profileSocial.profile.emptyValue')}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary min-h-11 px-4 text-sm"
                  data-testid="account-edit-profile"
                  onClick={() => {
                    setEditError(null)
                    setEditOpen(true)
                  }}
                >
                  <KuniriIcons.pencilSimple
                    size={19}
                    weight={kuniriIconWeight}
                    aria-hidden="true"
                  />
                  {t('profileSocial.profile.edit')}
                </button>
              </div>
            </div>

            <div className="rounded-[18px] border border-line bg-white p-4">
              <p className="font-bold text-deep">
                {t('screens.settings.password.title')}
              </p>
              <p className="mt-1 text-sm text-muted">
                {t('screens.settings.password.body')}
              </p>
              <button
                type="button"
                className="btn-secondary mt-3 min-h-11 px-4 text-sm"
                data-testid="account-change-password"
                disabled={passwordBusy}
                onClick={() => void handleSendPasswordLink()}
              >
                {t('screens.settings.password.cta')}
              </button>
              {passwordSent ? (
                <p
                  role="status"
                  data-testid="account-password-sent"
                  className="mt-2 text-sm font-semibold text-teal"
                >
                  {t('screens.settings.passwordLinkSent', {
                    email: profile.email,
                  })}
                </p>
              ) : null}
            </div>

            <div className="space-y-2 rounded-[18px] border border-line bg-white p-4">
              <button
                type="button"
                className="btn-ghost w-full text-danger hover:bg-[var(--danger-soft)]"
                data-testid="account-delete"
                disabled={deleteBusy}
                onClick={() => {
                  setDeleteError(null)
                  setDeleteOpen(true)
                }}
              >
                <KuniriIcons.userMinus
                  size={18}
                  weight={kuniriIconWeight}
                  aria-hidden="true"
                />
                {t('profileSocial.profile.deleteAccount')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {editOpen && profile ? (
        <EditProfileSheet
          profile={profile}
          busy={editBusy}
          errorKey={editError}
          onSave={(body) => void handleSave(body)}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        title={t('profileSocial.profile.deleteDialog.title')}
        body={t('profileSocial.profile.deleteDialog.body')}
        confirmLabel={t('profileSocial.profile.deleteDialog.confirm')}
        busyLabel={t('profileSocial.profile.deleteDialog.deleting')}
        busy={deleteBusy}
        danger
        errorKey={deleteError}
        onConfirm={() => void handleDelete()}
        onClose={() => setDeleteOpen(false)}
      />
    </section>
  )
}
